import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';
import { Action } from '@interstate/components/Action';

import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as contractingUtils from '@makemydeal/dr-dash-store/utils/contractingUtils';
import * as editCustomerActionCreators from '@makemydeal/dr-dash-store/actions/editCustomerActionCreators';
import * as customerRiskAndCoverageActionCreators from '@makemydeal/dr-dash-store/actions/customerRiskAndCoverageActionCreators';
import * as vehicleProtectionActionCreators from '@makemydeal/dr-dash-store/actions/vehicleProtectionActionCreators';
import {
    ContractFailureAlertContainer,
    ContractFailureReasons,
    ContractingFailureTitle,
    ContractAlertContent,
    ContractAlertTextSection,
    ContractAlertButtonSection
} from './ContractAlert.style';
import * as vehicleActionCreators from '@makemydeal/dr-dash-store/actions/vehicleActionCreators';
import * as baseDealSlideoutTypes from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as creditAppSearchActionCreators from '@makemydeal/dr-dash-store/actions/creditAppSearchActionCreators';
import { CONTRACTING_SAVE_FAILED_WITH_SPOT_ERRORS } from '@makemydeal/dr-dash-store/actionTypes/contractingActionTypes';
import {
    CONTRACT_ALERT_MESSAGE,
    CONTRACT_ALERT_TITLE,
    CONTRACT_ALERT_TITLE_READY_TO_GENERATE,
    CONTRACT_DETAILS_NEED_ATTENTION,
    CONTRACT_READY_TO_GENERATE_MESSAGE,
    VEHICLE_DETAILS_LINK_TEXT,
    VEHICLE_VALUE_MISMATCH_ACTION_PREFIX,
    VEHICLE_VALUE_MISMATCH_ACTION_SUFFIX,
    VEHICLE_VALUE_MISMATCH_MESSAGE
} from '../constants';
import { CONTRACTING_ERROR_MAP } from '../common/constants/contractingErrorMap';
import {
    BUYER,
    CO_BUYER,
    DealTabs,
    VEHICLE_INSURANCE,
    GAP,
    GAP_PRODUCT_CATEGORY_CODE,
    CONTRACT_API_ERRORED_VEHICLE_INSURANCE,
    CONTRACT_API_ERRORED_VPP,
    VEHICLE_ENTITY,
    ACCESSORIES,
    TRADE_IN,
    TRADE_INS
} from '@makemydeal/dr-shared-types';
import { useEffect } from 'react';
import { v4 as generateUuid } from 'uuid';
import { Button } from '@interstate/components/Button';
import { useHandleGenerateContract } from '../hooks/useGenerateContract';
import ContractFormTypeModal from '../generateContract/ContractFormTypeModal.interstate';

export const ContractFailureAlert = () => {
    const dispatch = useDispatch();
    const dealRefId = useSelector(offerSelectors.getDealRefId);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const alertInfo = useSelector(contractingSelectors.getFailureAlertInfo, shallowEqual);
    const failureEvent = useSelector(contractingSelectors.getFailureEvent);
    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);
    const isContractErrorMapping = useSelector(featureToggleSelectors.isContractErrorMappingEnabled);
    const isShowInsuranceInUnifiedCustomerCardEnabled = useSelector(
        featureToggleSelectors.isShowInsuranceInUnifiedCustomerCardEnabled
    );
    const isActivitiesCustomerCardEnabled = useSelector(featureToggleSelectors.isActivitiesCustomerCardEnabled);
    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
    const flattenedErrors = useSelector(contractingSelectors.getContractErrorList);
    const isAPIError = alertInfo.status === 'failure';
    const shouldRenderFieldValidationAlert = isGCDynamicValidationsEnabled ? isContractingContext : Boolean(flattenedErrors.length);
    const contractErrorListByEntity = useSelector(contractingSelectors.getContractErrorListByEntity);
    const contractApiErrorListByEntity = useSelector(contractingSelectors.getContractApiErrorListByEntity);
    const contractApiErroredVPPCode = useSelector(vehicleProtectionSelectors.getContractApiErroredVPP)?.productCode;
    const { placeholderTextMapping } = contractingUtils;
    const { handleGenerateContract, showContractFormTypeModal, handleConfirmContractFormType, handleCloseContractFormTypeModal } =
        useHandleGenerateContract();

    useEffect(() => {
        if (alertInfo.message) {
            const action = {
                dealRefId,
                dealXgId,
                alertUniqId: generateUuid(),
                totalReasons: alertInfo.reasons?.length || 0,
                ...alertInfo
            };
            if (Array.isArray(alertInfo.reasons)) {
                alertInfo.reasons.forEach((reason) => {
                    logNewRelicPageAction(`MV:Contracting - Displayed Contract Failure Alert`, { ...action, reason });
                });
            } else {
                logNewRelicPageAction(`MV:Contracting - Displayed Contract Failure Alert`, {
                    ...action,
                    reason: 'No Reason Provided'
                });
            }
        }
    }, [alertInfo.message]);

    if (!isAPIError && !shouldRenderFieldValidationAlert) {
        return null;
    }

    const reasonsToDisplay: string[] = isAPIError ? alertInfo.reasons ?? [] : flattenedErrors;

    const renderEntityAction = (entityKey: string) => {
        const handleActionClick = () => {
            switch (entityKey) {
                case BUYER:
                    dispatch(editCustomerActionCreators.showEditBuyerSlideout());
                    break;
                case CO_BUYER:
                    dispatch(editCustomerActionCreators.showEditCoBuyerSlideout());
                    break;
                case VEHICLE_INSURANCE:
                case CONTRACT_API_ERRORED_VEHICLE_INSURANCE:
                    if (isShowInsuranceInUnifiedCustomerCardEnabled && isActivitiesCustomerCardEnabled) {
                        dispatch(editCustomerActionCreators.showEditInsuranceSlideout());
                        break;
                    }
                    dispatch(baseDealActionCreators.setCustomerRiskAndCoverageTab('insurance'));
                    dispatch(baseDealActionCreators.setCustomerRiskAndCoverageSlideOutStatus(true));
                    dispatch(customerRiskAndCoverageActionCreators.customerRiskAndCoverageSlideOutButton());
                    break;
                case DealTabs.LENDER:
                    dispatch(baseDealActionCreators.openSlideOut(baseDealActionCreators.SlideOutType.Lender));
                    break;
                case ACCESSORIES:
                    dispatch(baseDealActionCreators.openSlideOut(baseDealActionCreators.SlideOutType.Accessories));
                    break;
                case GAP:
                    dispatch(baseDealActionCreators.openSlideOut(baseDealSlideoutTypes.SlideOutType.Protection));
                    dispatch(vehicleProtectionActionCreators.setEditableVppProductCode(GAP_PRODUCT_CATEGORY_CODE));
                    break;
                case CONTRACT_API_ERRORED_VPP:
                    dispatch(baseDealActionCreators.openSlideOut(baseDealSlideoutTypes.SlideOutType.Protection));
                    // Redirect to the product with the missing coverage term and open its Edit drawer.
                    if (contractApiErroredVPPCode) {
                        dispatch(vehicleProtectionActionCreators.setEditableVppProductCode(contractApiErroredVPPCode));
                    }
                    break;
                case VEHICLE_ENTITY:
                    dispatch(vehicleActionCreators.showEditVehicle());
                    break;
                case TRADE_IN:
                case TRADE_INS:
                    dispatch(baseDealActionCreators.openSlideOut(baseDealSlideoutTypes.SlideOutType.TradeIn));
                    break;
            }
        };

        return (
            <Action onClick={handleActionClick} data-testid="replace-text-action">
                <Typography variant="anchor-inline-sm" tag="a">
                    {placeholderTextMapping[entityKey]}
                </Typography>
            </Action>
        );
    };

    const title = isAPIError ? alertInfo.title : CONTRACT_ALERT_TITLE;
    const isAllErrorsFixed = !isAPIError && shouldRenderFieldValidationAlert && flattenedErrors.length === 0;
    const message = isAPIError ? alertInfo.message : isAllErrorsFixed ? CONTRACT_READY_TO_GENERATE_MESSAGE : CONTRACT_ALERT_MESSAGE;

    const renderVehicleValueMismatchReason = (vehicleValue: number, approvedAmount: number) => (
        <Typography variant="body-sm" tag="span" data-testid="vehicle-value-mismatch-reason">
            {VEHICLE_VALUE_MISMATCH_MESSAGE.replace('{approvedAmount}', formatDollarsAndCents(approvedAmount)).replace(
                '{vehicleValue}',
                formatDollarsAndCents(vehicleValue)
            )}{' '}
            <span style={{ display: 'block' }}>
                {VEHICLE_VALUE_MISMATCH_ACTION_PREFIX}{' '}
                <Action
                    onClick={() => {
                        dispatch(vehicleActionCreators.showEditVehicle());
                    }}
                    data-testid="vehicle-details-action"
                >
                    <Typography variant="anchor-inline-sm" tag="a">
                        {VEHICLE_DETAILS_LINK_TEXT}
                    </Typography>
                </Action>{' '}
                {VEHICLE_VALUE_MISMATCH_ACTION_SUFFIX}
            </span>
        </Typography>
    );

    const getReasonsToDisplay = (reasons: string[]) => {
        return reasons.map((reason, index) => {
            let mappedReason = reason;

            const vehicleValueMismatch = contractingUtils.parseVehicleValueMismatchReason(reason);
            if (vehicleValueMismatch) {
                return (
                    <li key={`${reason}-${index}`}>
                        {renderVehicleValueMismatchReason(vehicleValueMismatch.vehicleValue, vehicleValueMismatch.approvedAmount)}
                    </li>
                );
            }

            if (isContractErrorMapping) {
                const [key, value] = reason.split(':');
                const wildCardKey = key.replace(/\[\d+\]/g, '[?]');
                const mappedKey = CONTRACTING_ERROR_MAP[key] ?? CONTRACTING_ERROR_MAP[wildCardKey];
                if (mappedKey) {
                    mappedReason = `${mappedKey}:${value}`;
                }
            }
            return (
                <li key={`${reason}-${index}`}>
                    <Typography variant="body-sm">{mappedReason}</Typography>
                </li>
            );
        });
    };

    const errorsByEntity = isAPIError ? contractApiErrorListByEntity : isContractingContext && contractErrorListByEntity;
    const { other, cancellationTerms: cancellationTermsErrors, ...restEntityGroupings } = errorsByEntity || {};

    const mappedReasons =
        isAPIError || isContractingContext
            ? [
                  ...getReasonsToDisplay(reasonsToDisplay.filter(contractingUtils.parseVehicleValueMismatchReason)),
                  ...Object.keys(restEntityGroupings).map((entityKey) => {
                      const actionButton = renderEntityAction(entityKey);
                      const errorMsgWithAction = (
                          <Typography variant="body-sm" tag="span" sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                              {actionButton} {CONTRACT_DETAILS_NEED_ATTENTION}
                          </Typography>
                      );

                      return <li key={entityKey}>{errorMsgWithAction}</li>;
                  }),
                  ...getReasonsToDisplay(
                      [...(cancellationTermsErrors || []), ...(other || [])].filter(
                          (reason) => !contractingUtils.parseVehicleValueMismatchReason(reason)
                      )
                  )
              ]
            : getReasonsToDisplay(reasonsToDisplay);

    const openContractDeepLink = () => {
        logNewRelicPageAction('MV:Contracting - Clicked DT deep link', { dealRefId });
        if (failureEvent === CONTRACTING_SAVE_FAILED_WITH_SPOT_ERRORS) {
            dispatch(creditAppSearchActionCreators.openCreditAppSearchSlideout());
        } else {
            window.open(alertInfo.deepLinkUrl, '_blank');
        }
    };

    return (
        <>
            <ContractFailureAlertContainer>
                <Alert
                    type={isAllErrorsFixed ? 'success' : 'error'}
                    data-testid="contracting-failure"
                    sx={{ '.MuiAlert-message': { width: '100% !important' } }}
                    disableSuccessFade
                >
                    <ContractAlertContent>
                        <ContractAlertTextSection>
                            <ContractingFailureTitle variant="h6">
                                {!isAllErrorsFixed ? title : CONTRACT_ALERT_TITLE_READY_TO_GENERATE}
                            </ContractingFailureTitle>
                            <Typography variant="body-sm" sx={{ marginTop: '0.571rem' }}>
                                {message}
                                {alertInfo.deepLinkTexts && ' '}
                                {alertInfo.deepLinkTexts && (
                                    <Action onClick={openContractDeepLink} data-testid="contract-deep-link">
                                        <Typography variant="anchor-inline-sm" tag="a">
                                            {alertInfo.deepLinkTexts}
                                        </Typography>
                                    </Action>
                                )}
                            </Typography>
                            {mappedReasons.length > 0 && <ContractFailureReasons>{mappedReasons}</ContractFailureReasons>}
                        </ContractAlertTextSection>
                        <ContractAlertButtonSection>
                            <Button buttonStyle="secondary" size="small" onClick={handleGenerateContract}>
                                Regenerate Contract
                            </Button>
                        </ContractAlertButtonSection>
                    </ContractAlertContent>
                </Alert>
            </ContractFailureAlertContainer>
            <ContractFormTypeModal
                show={showContractFormTypeModal}
                onConfirm={handleConfirmContractFormType}
                onClose={handleCloseContractFormTypeModal}
            />
        </>
    );
};
